/* global getComputedStyle, requestAnimationFrame, MutationObserver, Event */
import flatpickr from 'flatpickr'

export default class FlatpickrManager {
  constructor (element, options = {}) {
    this.element = element
    this.instance = null
    this.timeSectionsElement = options.timeSectionsElement || null
    this.dateRangeContainerElement = options.dateRangeContainerElement || null
    this.options = {
      mode: 'range',
      dateFormat: 'Y-m-d',
      disable: [(date) => date > new Date()],
      ...options
    }
    this.stylesInjected = false
  }

  injectStyles () {
    if (this.stylesInjected) return
    this.stylesInjected = true
  }

  initialize (onChangeCallback = null, onOpenCallback = null, onCloseCallback = null) {
    if (!this.element || this.instance) {
      return
    }

    this.injectStyles()

    const config = { ...this.options }
    this.onOpenCallback = onOpenCallback
    this.onCloseCallback = onCloseCallback

    if (config.mode === 'range') {
      config.closeOnSelect = false
    }

    if (!config.appendTo) {
      config.appendTo = this.element.parentElement
    }

    config.static = false
    config.inline = false

    const originalOnReady = config.onReady
    config.onReady = (selectedDates, dateStr, instance) => {
      if (originalOnReady) {
        originalOnReady(selectedDates, dateStr, instance)
      }

      if (instance && instance.calendarContainer) {
        instance.calendarContainer.style.setProperty('border-radius', '8px', 'important')
        instance.calendarContainer.style.setProperty('overflow', 'hidden', 'important')
        instance.calendarContainer.style.setProperty('background', '#ffffff', 'important')
      }

      const positionCalendar = () => {
        if (instance && instance.calendarContainer) {
          const parent = instance.calendarContainer.parentElement

          if (parent) {
            if (getComputedStyle(parent).position === 'static') {
              parent.style.position = 'relative'
            }

            instance.calendarContainer.style.setProperty('position', 'absolute', 'important')
            instance.calendarContainer.style.setProperty('top', '100%', 'important')
            instance.calendarContainer.style.setProperty('left', '0', 'important')
            instance.calendarContainer.style.setProperty('margin-top', '4px', 'important')
            instance.calendarContainer.style.setProperty('transform', 'none', 'important')
            instance.calendarContainer.style.setProperty('z-index', '1000', 'important')
            instance.calendarContainer.style.setProperty('right', 'auto', 'important')
            instance.calendarContainer.style.setProperty('bottom', 'auto', 'important')
            instance.calendarContainer.style.setProperty('border-radius', '8px', 'important')
            instance.calendarContainer.style.setProperty('overflow', 'hidden', 'important')
          }
        }
      }

      positionCalendar()
      requestAnimationFrame(() => {
        positionCalendar()
        setTimeout(positionCalendar, 10)
        setTimeout(positionCalendar, 50)
      })

      if (instance && instance.calendarContainer) {
        const observer = new MutationObserver(() => {
          positionCalendar()
        })
        observer.observe(instance.calendarContainer, {
          attributes: true,
          attributeFilter: ['style', 'class']
        })
        instance._positionObserver = observer
      }
    }

    if (onChangeCallback) {
      const originalOnChange = config.onChange
      const originalOnClose = config.onClose

      config.onChange = (selectedDates, dateStr, instance) => {
        if (originalOnChange) {
          originalOnChange(selectedDates, dateStr, instance)
        }
      }

      config.onClose = (selectedDates, dateStr, instance) => {
        if (originalOnClose) {
          originalOnClose(selectedDates, dateStr, instance)
        }

        if (this.onCloseCallback) {
          this.onCloseCallback(selectedDates, dateStr, instance)
        }

        this.hideTimeSections()

        if (config.mode === 'range') {
          if (selectedDates && selectedDates.length > 0) {
            onChangeCallback(selectedDates, dateStr, instance)
          }
        } else {
          if (selectedDates && selectedDates.length > 0) {
            onChangeCallback(selectedDates, dateStr, instance)
          }
        }
      }

      const originalOnOpen = config.onOpen
      config.onOpen = (selectedDates, dateStr, instance) => {
        if (originalOnOpen) {
          originalOnOpen(selectedDates, dateStr, instance)
        }

        if (this.onOpenCallback) {
          this.onOpenCallback(selectedDates, dateStr, instance)
        }

        this.showTimeSections()

        const positionCalendar = () => {
          if (instance && instance.calendarContainer) {
            const parent = instance.calendarContainer.parentElement
            if (parent) {
              instance.calendarContainer.style.setProperty('position', 'absolute', 'important')
              instance.calendarContainer.style.setProperty('top', '100%', 'important')
              instance.calendarContainer.style.setProperty('left', '0', 'important')
              instance.calendarContainer.style.setProperty('margin-top', '4px', 'important')
              instance.calendarContainer.style.setProperty('transform', 'none', 'important')
              instance.calendarContainer.style.setProperty('z-index', '1000', 'important')
              instance.calendarContainer.style.setProperty('right', 'auto', 'important')
              instance.calendarContainer.style.setProperty('bottom', 'auto', 'important')
              instance.calendarContainer.style.setProperty('border-radius', '8px', 'important')
              instance.calendarContainer.style.setProperty('overflow', 'hidden', 'important')
            }
          }
        }

        requestAnimationFrame(() => {
          positionCalendar()
          setTimeout(positionCalendar, 10)
          setTimeout(positionCalendar, 50)
        })
      }
    }

    this.instance = flatpickr(this.element, config)

    return this.instance
  }

  destroy () {
    if (this.instance) {
      if (this.instance._positionObserver) {
        this.instance._positionObserver.disconnect()
        this.instance._positionObserver = null
      }
      this.instance.destroy()
      this.instance = null
    }
  }

  getInstance () {
    return this.instance
  }

  reinitialize (onChangeCallback = null, onOpenCallback = null, onCloseCallback = null) {
    this.destroy()
    return this.initialize(onChangeCallback, onOpenCallback, onCloseCallback)
  }

  showTimeSections () {
    if (this.timeSectionsElement && this.instance && this.instance.calendarContainer) {
      if (!this.instance.calendarContainer.contains(this.timeSectionsElement)) {
        this.instance.calendarContainer.appendChild(this.timeSectionsElement)
        this.timeSectionsElement.style.position = 'relative'
        this.timeSectionsElement.style.top = 'auto'
        this.timeSectionsElement.style.left = 'auto'
        this.timeSectionsElement.style.marginTop = '0'
        this.timeSectionsElement.style.width = '100%'
        this.timeSectionsElement.style.borderRadius = '0 0 8px 8px'
      }
      this.timeSectionsElement.style.display = 'block'

      this.convertAmPmToToggle()
    }
  }

  convertAmPmToToggle () {
    if (!this.timeSectionsElement) return

    const ampmSelects = this.timeSectionsElement.querySelectorAll('select[data-parking-session-filters-target*="AmPm"]')

    ampmSelects.forEach(select => {
      if (select.dataset.convertedToToggle === 'true') return

      const currentValue = select.value
      const wrapper = document.createElement('div')
      wrapper.className = 'ampm-toggle'
      wrapper.style.display = 'inline-flex'

      const amButton = document.createElement('button')
      amButton.textContent = 'AM'
      amButton.type = 'button'
      amButton.classList.toggle('active', currentValue === 'AM')
      amButton.onclick = () => {
        select.value = 'AM'
        select.dispatchEvent(new Event('change', { bubbles: true }))
        amButton.classList.add('active')
        pmButton.classList.remove('active')
      }

      const pmButton = document.createElement('button')
      pmButton.textContent = 'PM'
      pmButton.type = 'button'
      pmButton.classList.toggle('active', currentValue === 'PM')
      pmButton.onclick = () => {
        select.value = 'PM'
        select.dispatchEvent(new Event('change', { bubbles: true }))
        pmButton.classList.add('active')
        amButton.classList.remove('active')
      }

      const updateToggleState = () => {
        const value = select.value
        amButton.classList.toggle('active', value === 'AM')
        pmButton.classList.toggle('active', value === 'PM')
      }

      select.addEventListener('change', updateToggleState)

      let lastValue = select.value
      const checkValue = () => {
        if (select.value !== lastValue) {
          lastValue = select.value
          updateToggleState()
        }
      }

      setInterval(checkValue, 50)

      wrapper.appendChild(amButton)
      wrapper.appendChild(pmButton)

      select.style.display = 'none'
      select.style.position = 'absolute'
      select.style.opacity = '0'
      select.style.pointerEvents = 'none'
      select.dataset.convertedToToggle = 'true'

      select.parentNode.insertBefore(wrapper, select.nextSibling)
    })
  }

  hideTimeSections () {
    if (this.timeSectionsElement) {
      this.timeSectionsElement.style.display = 'none'
      if (this.dateRangeContainerElement && !this.dateRangeContainerElement.contains(this.timeSectionsElement)) {
        this.dateRangeContainerElement.appendChild(this.timeSectionsElement)
        this.timeSectionsElement.style.position = 'absolute'
        this.timeSectionsElement.style.top = '100%'
        this.timeSectionsElement.style.left = '0'
        this.timeSectionsElement.style.marginTop = '4px'
      }
    }
  }
}
